package co.maytes.standupbot.dashboard.features.settings.sections

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.motion.StateSwap
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonSize
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.RadioGroup
import co.maytes.standupbot.dashboard.components.widgets.RadioItem
import co.maytes.standupbot.dashboard.components.widgets.Switch
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.features.onboarding.IntegrationsStore
import co.maytes.standupbot.dashboard.features.onboarding.WatchedReposLoadState
import co.maytes.standupbot.dashboard.features.settings.LocalSettingsNavigate
import co.maytes.standupbot.dashboard.features.settings.Plain
import co.maytes.standupbot.dashboard.features.settings.ReleasePanel
import co.maytes.standupbot.dashboard.features.settings.ReleaseSettingsEditor
import co.maytes.standupbot.dashboard.features.settings.ReleasesCopy
import co.maytes.standupbot.dashboard.features.settings.ReleasesLabelInput
import co.maytes.standupbot.dashboard.features.settings.ReleasesLabels
import co.maytes.standupbot.dashboard.features.settings.ReleasesRepoMode
import co.maytes.standupbot.dashboard.features.settings.ReleasesRepoPicker
import co.maytes.standupbot.dashboard.features.settings.ReleasesRepoPickerPanel
import co.maytes.standupbot.dashboard.features.settings.SettingsBars
import co.maytes.standupbot.dashboard.features.settings.SettingsEmpty
import co.maytes.standupbot.dashboard.features.settings.SettingsError
import co.maytes.standupbot.dashboard.features.settings.SettingsField
import co.maytes.standupbot.dashboard.features.settings.SettingsGroup
import co.maytes.standupbot.dashboard.features.settings.SettingsPage
import co.maytes.standupbot.dashboard.features.settings.SettingsPanel
import co.maytes.standupbot.dashboard.features.settings.SettingsPreview
import co.maytes.standupbot.dashboard.features.settings.SettingsRowItem
import co.maytes.standupbot.dashboard.features.settings.SettingsSaveBar
import co.maytes.standupbot.dashboard.features.settings.SettingsSectionRow
import co.maytes.standupbot.dashboard.features.settings.SettingsSubtitle
import co.maytes.standupbot.dashboard.features.settings.StatePhase
import co.maytes.standupbot.dashboard.features.settings.Strong
import co.maytes.standupbot.dashboard.features.settings.rememberReleaseSettingsEditor
import co.maytes.standupbot.dashboard.stores.rememberApiClient
import co.maytes.standupbot.dashboard.stores.rememberAuthStore
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.shared.dto.ship.ReleaseSettingsDto
import co.maytes.standupbot.shared.dto.ship.UpdateReleaseSettingsRequestDto
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Div

private const val FEED_ANCHOR = "release-feed"
private const val REPOSITORIES_ANCHOR = "repositories"
private const val LABELS_ANCHOR = "hold-back-labels"
private const val UNSAVED_HINT = "Unsaved changes"
private const val PREVIEW_LABEL = "What this does"
private const val LOADING_ROWS = 6

@Composable
fun ReleasesSection() {
    val auth = rememberAuthStore()
    val api = rememberApiClient()
    val editor = rememberReleaseSettingsEditor()
    val integrations = remember(api) { IntegrationsStore(api) }
    val workspaceId = auth.currentUser?.workspace?.id

    LaunchedEffect(workspaceId) {
        if (!workspaceId.isNullOrBlank()) integrations.loadGithubRepos(workspaceId)
    }
    LaunchedEffect(editor) { editor.load() }

    val settings = editor.loaded
    val ready = integrations.watchedReposState == WatchedReposLoadState.Ready
    val watchedCount = integrations.watchedRepos.size.takeIf { ready }
    val availableCount = integrations.availableRepos.size.takeIf { ready }
    SettingsPage(
        group = SettingsGroup.Workspace,
        title = "Release notes",
        subtitle = settings?.let {
            {
                val copy = ReleasesCopy.subtitle(it, watchedCount, availableCount)
                SettingsSubtitle {
                    Strong(copy.lead)
                    Plain(copy.rest)
                }
            }
        },
    ) {
        when {
            settings != null -> ReleasesForm(editor, settings, integrations, workspaceId, watchedCount)
            editor.loadError != null -> ReleasesFailed(editor::reload)
            else -> ReleasesLoading()
        }
    }
}

@Composable
private fun ReleasesLoading() {
    listOf(
        Triple(FEED_ANCHOR, "Release feed", ReleasesCopy.FEED_SECTION_DESCRIPTION),
        Triple(REPOSITORIES_ANCHOR, "Repositories", ReleasesCopy.REPOSITORIES_SECTION_DESCRIPTION),
        Triple(LABELS_ANCHOR, "Hold back", ReleasesCopy.LABELS_SECTION_DESCRIPTION),
    ).forEach { (anchor, title, description) ->
        SettingsSectionRow(anchor = anchor, title = title, description = description) {
            SettingsPanel { SettingsBars(rows = 3) }
        }
    }
}

@Composable
private fun ReleasesFailed(onRetry: () -> Unit) {
    SettingsSectionRow(
        anchor = FEED_ANCHOR,
        title = "Release feed",
        description = ReleasesCopy.FEED_SECTION_DESCRIPTION,
    ) {
        SettingsPanel { SettingsError(what = "the release settings", onRetry = onRetry) }
    }
}

@Composable
private fun ReleasesForm(
    editor: ReleaseSettingsEditor,
    settings: ReleaseSettingsDto,
    integrations: IntegrationsStore,
    workspaceId: String?,
    watchedCount: Int?,
) {
    var enabled by remember(settings) { mutableStateOf(settings.mergedFeedEnabled) }
    var mode by remember(settings) { mutableStateOf(ReleasesRepoPicker.modeOf(settings.trackedRepos)) }
    var selected by remember(settings) { mutableStateOf(settings.trackedRepos.toSet()) }
    var labels by remember(settings) { mutableStateOf(settings.holdBackLabels) }
    var labelDraft by remember(settings) { mutableStateOf("") }

    val trackedPayload = ReleasesRepoPicker.payload(mode, selected)
    val labelsPayload = ReleasesLabels.effective(labels, labelDraft)
    val dirty = enabled != settings.mergedFeedEnabled ||
        trackedPayload.toSet() != settings.trackedRepos.toSet() ||
        labelsPayload != settings.holdBackLabels

    FeedSection(editor, settings, enabled) { enabled = it }
    RepositoriesSection(
        settings = settings,
        integrations = integrations,
        workspaceId = workspaceId,
        watchedCount = watchedCount,
        mode = mode,
        onMode = { mode = it },
        selected = selected,
        onSelected = { selected = it },
    )
    LabelsSection(labelsPayload, labels, labelDraft, { labels = it }, { labelDraft = it })

    Div(
        attrs = Modifier
            .styleModifier {
                property("position", "sticky")
                property("bottom", "0")
                if (dirty) {
                    property("background-color", Palette.background.cssRgb())
                    property("border-top", "1px solid ${Palette.borderSubtle.cssRgb()}")
                    property("padding-bottom", Spacing.sm.toString())
                }
            }
            .toAttrs(),
    ) {
        SettingsSaveBar(
            open = dirty,
            saving = editor.saving == ReleasePanel.Page,
            canSave = editor.saving == null,
            hint = editor.failure?.takeIf { it.panel == ReleasePanel.Page }?.message ?: UNSAVED_HINT,
            onSave = {
                editor.save(ReleasePanel.Page, UpdateReleaseSettingsRequestDto(enabled, trackedPayload, labelsPayload))
            },
            onReset = {
                enabled = settings.mergedFeedEnabled
                mode = ReleasesRepoPicker.modeOf(settings.trackedRepos)
                selected = settings.trackedRepos.toSet()
                labels = settings.holdBackLabels
                labelDraft = ""
            },
        )
    }
}

@Composable
private fun FeedSection(
    editor: ReleaseSettingsEditor,
    settings: ReleaseSettingsDto,
    enabled: Boolean,
    onEnabled: (Boolean) -> Unit,
) {
    SettingsSectionRow(
        anchor = FEED_ANCHOR,
        title = "Release feed",
        description = ReleasesCopy.FEED_SECTION_DESCRIPTION,
    ) {
        SettingsPanel {
            SettingsRowItem(
                title = ReleasesCopy.FEED_SWITCH_TITLE,
                first = true,
                description = ReleasesCopy.feedStatus(enabled),
                trailing = {
                    Switch(
                        checked = enabled,
                        onCheckedChange = onEnabled,
                        ariaLabel = ReleasesCopy.FEED_SWITCH_TITLE,
                    )
                },
            )
            SettingsRowItem(
                title = ReleasesCopy.BACKFILL_TITLE,
                description = if (settings.mergedFeedEnabled) ReleasesCopy.BACKFILL_READY else ReleasesCopy.BACKFILL_BLOCKED,
                trailing = {
                    Button(
                        label = if (editor.backfilling) "Starting…" else "Backfill last 30 days",
                        variant = ButtonVariant.Secondary,
                        size = ButtonSize.Sm,
                        enabled = !editor.backfilling && settings.mergedFeedEnabled,
                        onClick = editor::backfill,
                    )
                },
            )
        }
    }
}

@Composable
private fun RepositoriesSection(
    settings: ReleaseSettingsDto,
    integrations: IntegrationsStore,
    workspaceId: String?,
    watchedCount: Int?,
    mode: ReleasesRepoMode,
    onMode: (ReleasesRepoMode) -> Unit,
    selected: Set<String>,
    onSelected: (Set<String>) -> Unit,
) {
    val ready = integrations.watchedReposState == WatchedReposLoadState.Ready
    val aside = if (ready && mode == ReleasesRepoMode.Picked) {
        listOf("Following" to "${selected.size} of ${integrations.availableRepos.size}")
    } else {
        emptyList()
    }
    SettingsSectionRow(
        anchor = REPOSITORIES_ANCHOR,
        title = "Repositories",
        description = ReleasesCopy.REPOSITORIES_SECTION_DESCRIPTION,
        aside = aside,
    ) {
        SettingsPanel {
            RadioGroup(
                value = mode,
                items = listOf(
                    RadioItem(
                        ReleasesRepoMode.Standups,
                        ReleasesCopy.modeStandupsLabel(watchedCount),
                        ReleasesCopy.modeStandupsDescription(watchedCount),
                    ),
                    RadioItem(ReleasesRepoMode.Picked, ReleasesCopy.MODE_PICKED_LABEL, ReleasesCopy.MODE_PICKED_DESCRIPTION),
                ),
                onValueChange = onMode,
                label = ReleasesCopy.MODE_LABEL,
            )
            if (mode == ReleasesRepoMode.Picked) {
                PickerArea(settings, integrations, workspaceId, selected, onSelected)
            }
            SettingsPreview(label = PREVIEW_LABEL) {
                Plain(ReleasesCopy.repositoryPreview(mode, ReleasesRepoPicker.payload(ReleasesRepoMode.Picked, selected), watchedCount))
            }
        }
    }
}

@Composable
private fun PickerArea(
    settings: ReleaseSettingsDto,
    integrations: IntegrationsStore,
    workspaceId: String?,
    selected: Set<String>,
    onSelected: (Set<String>) -> Unit,
) {
    val scope = rememberCoroutineScope()
    val navigate = LocalSettingsNavigate.current
    val phase = integrations.watchedReposState.releasesPhase()
    val items = remember(integrations.availableRepos, settings.trackedRepos) {
        ReleasesRepoPicker.items(integrations.availableRepos, settings.trackedRepos)
    }
    StateSwap(state = phase) {
        when {
            phase == StatePhase.Loading -> SettingsBars(rows = LOADING_ROWS)
            phase == StatePhase.Error -> SettingsError(
                what = "your repositories",
                onRetry = { if (!workspaceId.isNullOrBlank()) scope.launch { integrations.loadGithubRepos(workspaceId) } },
            )
            items.isEmpty() -> SettingsEmpty(
                text = ReleasesCopy.NO_REPOSITORIES,
                action = {
                    Button(
                        label = "Open integrations",
                        variant = ButtonVariant.Secondary,
                        size = ButtonSize.Sm,
                        onClick = { navigate("integrations") },
                    )
                },
            )
            else -> ReleasesRepoPickerPanel(items, selected, onSelected)
        }
    }
}

@Composable
private fun LabelsSection(
    labelsPayload: List<String>,
    labels: List<String>,
    draft: String,
    onLabels: (List<String>) -> Unit,
    onDraft: (String) -> Unit,
) {
    SettingsSectionRow(
        anchor = LABELS_ANCHOR,
        title = "Hold back",
        description = ReleasesCopy.LABELS_SECTION_DESCRIPTION,
    ) {
        SettingsPanel {
            SettingsField(label = "Labels", help = ReleasesCopy.LABELS_HELP) {
                ReleasesLabelInput(labels, draft, onLabels, onDraft)
            }
            SettingsPreview(label = PREVIEW_LABEL) { Plain(ReleasesCopy.holdBackPreview(labelsPayload)) }
        }
    }
}

private fun WatchedReposLoadState.releasesPhase(): StatePhase = when (this) {
    WatchedReposLoadState.Idle, WatchedReposLoadState.Loading -> StatePhase.Loading
    is WatchedReposLoadState.Error -> StatePhase.Error
    WatchedReposLoadState.Ready -> StatePhase.Ready
}
